import { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, Star, Quote } from "lucide-react";
import { HOME_TESTIMONIALS, ASSETS } from "@/data/site";
import { Reveal, SectionLabel } from "@/components/Reveal";

const GoogleG = () => (
  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white">
    <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
      <path fill="#4285F4" d="M22.5 12.2c0-.7-.1-1.4-.2-2H12v3.8h5.9a5 5 0 0 1-2.2 3.3v2.7h3.6c2.1-2 3.2-4.9 3.2-7.8Z" />
      <path fill="#34A853" d="M12 23c2.9 0 5.4-1 7.3-2.7l-3.6-2.7c-1 .7-2.3 1.1-3.7 1.1-2.8 0-5.2-1.9-6.1-4.5H2.2v2.8A11 11 0 0 0 12 23Z" />
      <path fill="#FBBC05" d="M5.9 14.2a6.6 6.6 0 0 1 0-4.2V7.2H2.2a11 11 0 0 0 0 9.8l3.7-2.8Z" />
      <path fill="#EA4335" d="M12 5.4c1.6 0 3 .6 4.1 1.6l3.1-3.1A11 11 0 0 0 2.2 7.2L5.9 10c.9-2.6 3.3-4.6 6.1-4.6Z" />
    </svg>
  </span>
);

const Card = ({ t }) => (
  <div className="flex h-full flex-col rounded-3xl bg-white p-8 shadow-[0_20px_50px_-25px_rgba(8,38,66,0.45)]">
    <Quote className="h-11 w-11 shrink-0 text-[#1F6FB2]" fill="currentColor" strokeWidth={0} />
    <p className="mt-5 flex-1 text-[15px] leading-relaxed text-[#334E68]">{t.quote}</p>
    <div className="mt-7 flex items-center gap-3 border-t border-slate-100 pt-5">
      {t.google && <GoogleG />}
      <div>
        <div className="flex items-center gap-1">
          <span className="text-sm font-bold text-[#102A43]">{t.rating.toFixed(1)}</span>
          <span className="flex">
            {Array.from({ length: 5 }).map((_, i) => (
              <Star key={i} className="h-3.5 w-3.5 text-[#F2A900]" fill="currentColor" strokeWidth={0} />
            ))}
          </span>
        </div>
        <div className="font-display text-base font-semibold text-[#102A43]">{t.name}</div>
      </div>
    </div>
  </div>
);

export const Testimonials = () => {
  const [perView, setPerView] = useState(3);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const calc = () => {
      const w = window.innerWidth;
      setPerView(w < 768 ? 1 : w < 1024 ? 2 : 3);
    };
    calc();
    window.addEventListener("resize", calc);
    return () => window.removeEventListener("resize", calc);
  }, []);

  const maxIndex = Math.max(0, HOME_TESTIMONIALS.length - perView);
  useEffect(() => { if (index > maxIndex) setIndex(maxIndex); }, [maxIndex, index]);

  const prev = () => setIndex((i) => Math.max(0, i - 1));
  const next = () => setIndex((i) => Math.min(maxIndex, i + 1));

  return (
    <section className="relative overflow-hidden py-20 md:py-28" data-testid="testimonials">
      <div className="absolute inset-0">
        <img src={ASSETS.vanHero} alt="" aria-hidden="true" className="h-full w-full object-cover" />
        <div className="absolute inset-0" style={{ background: "rgba(8,38,66,0.82)" }} />
      </div>

      <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <Reveal>
            <SectionLabel dark>Our Testimonials</SectionLabel>
            <h2 className="mt-5 max-w-2xl font-display text-3xl md:text-5xl font-light tracking-tight text-white text-balance">
              What our <span className="font-extrabold">satisfied customers</span> say about us
            </h2>
            <p className="mt-5 max-w-xl text-[15px] leading-relaxed text-[#E6EDF5]/85">
              From air conditioning installations to electrical upgrades and heat pump hot water systems, our team focuses on professional workmanship, reliable service and practical solutions for every property.
            </p>
          </Reveal>

          <div className="flex gap-3">
            <button
              onClick={prev}
              disabled={index === 0}
              data-testid="testimonial-prev"
              aria-label="Previous testimonials"
              className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#0B4F8A] text-white transition hover:bg-[#18558A] disabled:opacity-40"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              onClick={next}
              disabled={index === maxIndex}
              data-testid="testimonial-next"
              aria-label="Next testimonials"
              className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#0B4F8A] text-white transition hover:bg-[#18558A] disabled:opacity-40"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="mt-12 overflow-hidden">
          <div
            className="flex transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]"
            style={{ transform: `translateX(-${index * (100 / perView)}%)` }}
          >
            {HOME_TESTIMONIALS.map((t) => (
              <div key={t.name} className="shrink-0 px-3" style={{ width: `${100 / perView}%` }}>
                <Card t={t} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
